import { useAuth0 } from "@auth0/auth0-react";
import moment, { Moment } from "moment";
import SplineApi from "../../tools/spline-api.js";
import * as Parse from "papaparse";
import useDealBuilderAAAStore from "../../stores/deal-builder-aaa.store.ts";

let inFlightAAARequest: Promise<[Moment, number[]][]> | null = null;

export default function useEODAAA() {
  const { getAccessTokenSilently } = useAuth0();
  const { aaa, setAAA } = useDealBuilderAAAStore();

  async function fetchEodAAA(): Promise<[Moment, number[]][]> {
    const token = await getAccessTokenSilently();
    const splineApi = new SplineApi(token);
    const csv = await splineApi.getAAAData();
    const csvRows = Parse.parse(csv).data.filter((x) => x[0] !== "");

    const formattedRows: [Moment, number[]][] = [];
    for (let i = 1; i < csvRows.length; i++) {
      const row = csvRows[i];
      const date = moment(row[0], "YYYY-MM-DD");
      const values = row.slice(1).map((x) => x / 100);
      formattedRows.push([date, values]);
    }

    return formattedRows;
  }

  async function getEODAAA(): Promise<[Moment, number[]][]> {
    if (aaa.length !== 0) return aaa;

    if (!inFlightAAARequest) {
      inFlightAAARequest = fetchEodAAA().then((newAAA) => {
        setAAA(newAAA);
        inFlightAAARequest = null;
        return newAAA;
      }).catch((err) => {
        inFlightAAARequest = null;
        throw err;
      });
    }

    return inFlightAAARequest;
  }

  async function getAlignedAAA(
    marketConditionsDate: Moment,
    principalDates: string[]
  ): Promise<[number[], any]> {
    const realTimeAAA = await getEODAAA();
    const date = realTimeAAA[realTimeAAA.length - 1][0];
    const aaaTenors = realTimeAAA[realTimeAAA.length - 1][1];
    const yields: number[] = [];

    for (const dateStr of principalDates) {
      const maturityDate = moment(dateStr);
      const yearsToMaturity = Math.round(
        maturityDate.diff(marketConditionsDate, "years", true)
      );

      const boundedYearsToMaturity = Math.max(1, yearsToMaturity);
      yields.push(aaaTenors[boundedYearsToMaturity - 1]);
    }

    const dateStr = date.format("MM/DD/YYYY");
    const title = '"AAA"';
    const description = "Spline Data EOD (AAA, GO, 5%, Large) " + dateStr + ".";
    return [yields, { title, description }];
  }

  async function getDateRates(date: Moment): Promise<number[] | undefined> {
    const realTimeAAA = await getEODAAA();
    const entry = realTimeAAA.find(x => x[0].isSame(date, "day"));
    return entry?.[1];
  }

  async function getAAARateForSavedDeal(
    bidDate: Moment,
    yearsToMaturity: number
  ): Promise<number | undefined> {
    const rates = await getDateRates(bidDate);
    return rates?.[yearsToMaturity - 1];
  }

  return { getAlignedAAA, getAAARateForSavedDeal };
}
